import { useEffect, useState } from "react";
import { FC } from "react";
import { Button, Card, Col, Form, Row, Spinner, Stack } from "react-bootstrap";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import {
  Errors,
  MetaTagsFormData,
  MetaTagsHeader,
  MetaTagsResult,
  UrlKeywords,
} from "./types";
import { getAiMetaTagsData } from "../../../services/getAiResponse";
import { MetaResult } from "./MetaResults";
import { ToolsState, useToolsStore } from "../../../store/useToolsStore";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";
import { Box } from "./metaTagsStyle";
import {
  isValidWebsiteUrl,
  isValidMetaKeywords,
  isValidWebsiteName,
  isValidCreativity,
} from "../../../utils/validators";


type Props = {};

export const MetaTagsGenerator: FC<Props> = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState<Errors | undefined>();

  const {
    setShowResult,
    showResult,
    formData,
    setResult,
    setFormData,
    result,
  } = useToolsStore<ToolsState>((state) => state);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [result, formData?.urlKeywords]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValid() || !formData) {
      return;
    }

    setShowLoader(true);
    try {
      const response = await getAiMetaTagsData(formData);
      if (response) {
        const data: MetaTagsResult[] = JSON.parse(
          response.replaceAll("```json", "").replaceAll("```", "")
        );
        setResult(data);
        setShowResult(true);
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      }
    } catch (error) {
      console.error("Error generating meta tags:", error);
    } finally {
      setShowLoader(false);
    }
  };

  const headerInputChange = (field: keyof MetaTagsHeader, value: string) => {
    if (!formData) return;

    const updatedForm: MetaTagsFormData = {
      ...formData,
      header: {
        ...formData.header,
        [field]: field === "results" ? Number(value) : value,
      },
    };

    setFormData(updatedForm);
  };

  // const isValid = () => {
  //   if (!formData) return false;

  //   const urlKeywords = formData.urlKeywords.map((x) => ({
  //     keywords: !x.keywords.trim(),
  //     websiteUrl: !x.websiteUrl.trim(),
  //   }));

  //   const validationErrors: Errors = {
  //     header: {
  //       creativity: !formData.header.creativity.trim(),
  //       websiteName: !formData.header.websiteName.trim(),
  //       results: (formData.header.results ?? 0) < 1,
  //     },
  //     urlKeywords: urlKeywords,
  //   };
  //   setErrors(validationErrors);

  //   return (
  //     !validationErrors.header.creativity &&
  //     !validationErrors.header.results &&
  //     !validationErrors.header.websiteName &&
  //     validationErrors.urlKeywords.every(
  //       (head) => !head.keywords && !head.websiteUrl
  //     )
  //   );
  // };

  const isValid = () => {
  if (!formData) return false;

  const urlKeywordsErrors = formData.urlKeywords.map(item => ({
    websiteUrl: !isValidWebsiteUrl(item.websiteUrl),
    keywords: !isValidMetaKeywords(item.keywords),
  }));

  const validationErrors: Errors = {
    header: {
      websiteName: !isValidWebsiteName(formData.header.websiteName),
      creativity: !isValidCreativity(formData.header.creativity),
      results: (formData.header.results ?? 0) < 1,
    },
    urlKeywords: urlKeywordsErrors,
  };

  setErrors(validationErrors);

  return (
    !validationErrors.header.websiteName &&
    !validationErrors.header.creativity &&
    !validationErrors.header.results &&
    validationErrors.urlKeywords.every(
      err => !err.websiteUrl && !err.keywords
    )
  );
};



  const handleAddMore = () => {
    if (!formData) return;
    const updatedForm: MetaTagsFormData = {
      ...formData,
      urlKeywords: [...formData.urlKeywords, { keywords: "", websiteUrl: "" }],
    };
    setFormData(updatedForm);
  };

  const handleRemove = (index: number) => {
    if (!formData || formData.urlKeywords.length <= 1) return;
    const updatedForm: MetaTagsFormData = {
      ...formData,
      urlKeywords: formData.urlKeywords.filter((_, i) => i !== index),
    };
    setFormData(updatedForm);
  };

  const handleInputChange = (
    index: number,
    field: keyof UrlKeywords,
    value: string
  ) => {
    if (!formData) return;
    const updatedForm: MetaTagsFormData = {
      ...formData,
      urlKeywords: formData.urlKeywords.map((input, i) =>
        i === index ? { ...input, [field]: value } : input
      ),
    };
    setFormData(updatedForm);
  };

  return (
    <Box>
      <div style={{}}>
        {/* {!showResult && ( */}
        <Row>
          <Col>
            <Form onSubmit={handleSubmit} id="metaTagGenerators">
              {!isInIframe && (
                <ToolsHeader
                  title="Meta Tags Generator"
                  description="Generate SEO-Optimized meta titles and descriptions based on the
                targeted keyword."
                />
              )}
              <Card
                style={{
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Website Name*</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Website Name"
                        value={formData?.header.websiteName || ""}
                        onChange={(e) =>
                          headerInputChange("websiteName", e.target.value)
                        }
                        isInvalid={errors?.header.websiteName}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Creativity</Form.Label>
                          <Form.Select
                            className="metaTagsCreativeSelect"
                            isInvalid={errors?.header.creativity}
                            value={formData?.header.creativity || ""}
                            onChange={(e) =>
                              headerInputChange("creativity", e.target.value)
                            }
                          >
                            <option value="" disabled>
                              Select Creativity
                            </option>
                            <option value="Regular">Regular</option>
                            <option value="High">High</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Results</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={formData?.header.results || ""}
                            onChange={(e) =>
                              headerInputChange("results", e.target.value)
                            }
                            isInvalid={errors?.header.results}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {formData?.urlKeywords.map((input: UrlKeywords, index) => (
                  <Row key={index} style={{ paddingTop: "10px" }}>
                    <Col sm={6}>
                      {index === 0 && <Form.Label>Website URLs*</Form.Label>}
                      <Form.Group
                        controlId={`formWebsiteUrls-${index}`}
                        className="bottomMbWeb"
                      >
                        <Form.Control
                          type="text"
                          placeholder="Enter Website URLs"
                          value={input.websiteUrl}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "websiteUrl",
                              e.target.value
                            )
                          }
                          isInvalid={errors?.urlKeywords?.[index]?.websiteUrl}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      {index === 0 && <Form.Label>Keywords*</Form.Label>}
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="bottomMbWeb"
                      >
                        <Form.Group
                          style={{ flex: 1 }}
                          controlId={`formKeywords-${index}`}
                        >
                          <Form.Control
                            type="text"
                            placeholder="Enter keywords separated by comma"
                            value={input.keywords}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "keywords",
                                e.target.value
                              )
                            }
                            isInvalid={errors?.urlKeywords?.[index]?.keywords}
                          />
                        </Form.Group>
                        <Button
                          className="buttonDeleteMeta"
                          variant="outline-danger"
                          // size="sm"
                          onClick={() => handleRemove(index)}
                          disabled={formData.urlKeywords.length === 1}
                        >
                          <FaTrashAlt />
                        </Button>
                      </Stack>
                    </Col>
                  </Row>
                ))}
                <Row style={{ paddingTop: "10px" }}>
                  <Col>
                    <Button onClick={handleAddMore} className="meta-add-btn">
                      <FaPlus /> Add More
                    </Button>
                  </Col>
                </Row>
                <Row style={{ textAlign: "center" }}>
                  <Col className="mt-2">
                    {showLoader ? (
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      <Button className="meta-generate-btn" type="submit">
                        Generate Tags
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card>
            </Form>
          </Col>
        </Row>
        {/* )} */}
        {/* {showResult && ( */}
        <Row style={{ paddingTop: "10px" }}>
          <Col>
            {/* <Button
            onClick={() => setShowResult(false)}
            variant="primary"
            className="mb-3"
          >
            Back to Meta Tags Generator
          </Button> */}
            <MetaResult formData={formData} />
          </Col>
        </Row>
        {/* )} */}
      </div>
    </Box>
  );
};
